import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, hexToRgba, resolveLinkProps, isColorDark } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function TeamCultureSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'perks-first';

    const perks = content.perks || [];
    const photos = content.photos || [];
    const quotes = content.employee_quotes || [];

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Perks grid ──
    const PerksGrid = () => perks.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {perks.map((perk, i) => (
                <div key={i} className={`${card} p-6 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                    {perk.icon && (
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-4"
                            style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                            {perk.icon}
                        </div>
                    )}
                    <h4 className="text-base font-bold mb-2" style={{ color: s.text }}>{perk.title}</h4>
                    {perk.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{perk.description}</p>}
                </div>
            ))}
        </div>
    );

    // ── Photos collage ──
    const PhotosCollage = () => photos.length > 0 && (
        <div className="mb-14">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.map((photo, i) => {
                    const tall = i % 5 === 0 || i % 5 === 3;
                    return (
                        <div key={i} className={`${tall ? 'row-span-2' : ''} overflow-hidden rounded-2xl group transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                            style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}>
                            <img src={photo.image_url} alt="" loading="lazy"
                                className={`w-full ${tall ? 'h-full min-h-[280px]' : 'h-44 md:h-52'} object-cover group-hover:scale-110 transition-transform duration-700`} />
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // ── Employee quotes ──
    const EmployeeQuotes = () => quotes.length > 0 && (
        <div className="mb-10">
            <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
                {quotes.map((quote, i) => (
                    <div key={i} className={`${card} p-6 min-w-[280px] max-w-[340px] shrink-0 snap-start transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                        style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms' }}>
                        <svg className="w-6 h-6 mb-3" style={{ color: hexToRgba(s.accent, 0.3) }} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11h4v10H0z" />
                        </svg>
                        <p className="text-sm leading-relaxed italic mb-4" style={{ color: s.textSub }}>"{quote.text}"</p>
                        <div className="flex items-center gap-3 mt-auto">
                            {quote.image_url ? (
                                <img src={quote.image_url} alt={quote.name || ''} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                            ) : (
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                                    style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>
                                    {quote.name?.charAt(0) || '?'}
                                </div>
                            )}
                            <div>
                                <p className="text-sm font-semibold" style={{ color: s.text }}>{quote.name}</p>
                                {quote.role && <p className="text-xs" style={{ color: s.accent }}>{quote.role}</p>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // ── CTA link ──
    const CtaLink = () => content.open_positions_link && (
        <div className="text-center mt-10">
            <a {...resolveLinkProps(content.open_positions_link)}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold text-white transition-all hover:scale-105 hover:shadow-lg"
                style={{ backgroundColor: s.accent }}>
                {content.open_positions_text || 'Ver posiciones abiertas'}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
            </a>
        </div>
    );

    // ── Variant layouts ──
    const renderPerksFirst = () => (
        <>
            <PerksGrid />
            <PhotosCollage />
            <EmployeeQuotes />
        </>
    );

    const renderPhotosFirst = () => (
        <>
            <PhotosCollage />
            <PerksGrid />
            <EmployeeQuotes />
        </>
    );

    const renderBalanced = () => (
        <>
            {perks.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14 items-center">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {perks.map((perk, i) => (
                            <div key={i} className={`${card} p-5 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                {perk.icon && (
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl mb-3"
                                        style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                                        {perk.icon}
                                    </div>
                                )}
                                <h4 className="text-sm font-bold mb-1" style={{ color: s.text }}>{perk.title}</h4>
                                {perk.description && <p className="text-xs leading-relaxed" style={{ color: s.textSub }}>{perk.description}</p>}
                            </div>
                        ))}
                    </div>
                    {photos.length > 0 && (
                        <div className="grid grid-cols-2 gap-3">
                            {photos.slice(0, 4).map((photo, i) => (
                                <div key={i} className={`overflow-hidden rounded-2xl group ${i === 0 ? 'col-span-2' : ''}`}>
                                    <img src={photo.image_url} alt="" loading="lazy"
                                        className={`w-full ${i === 0 ? 'h-52' : 'h-36'} object-cover group-hover:scale-110 transition-transform duration-700`} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            <EmployeeQuotes />
        </>
    );

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                {content.description && (
                    <p className="text-center text-base leading-relaxed max-w-3xl mx-auto mb-12" style={{ color: s.textSub }}>
                        {content.description}
                    </p>
                )}
                {variant === 'photos-first' ? renderPhotosFirst() : variant === 'balanced' ? renderBalanced() : renderPerksFirst()}
                <CtaLink />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
